import classNames from 'classnames'

import type { ReactElement } from 'react'
import type { APP_USER_STATUS } from '~/types/app-user'
import type { ASSET_STATUS } from '~/types/assets-management'
import type { CHECKLIST_STATUS } from '~/types/checklist'
import type { TAppEventType } from '~/types/event'
import type { IAP_SUB_TYPE, IAP_TYPE, IAP_STATUS, IAP_PERIODICITY } from '~/types/iap'
import type { NAVIGATION_GRAPH_STATUS } from '~/types/navigation-graph'
import type { PAGE_CONTAINER_STATUS } from '~/types/page-container'
import type { REPORT_COMMENT_STATUS, REPORT_REASON, REPORT_STATUS } from '~/types/report'
import type { ML_LABEL, REVIEW_STATUS } from '~/types/review'
import type { TEMPLATE_STATUS, TEMPLATE_TYPE } from '~/types/template'
import type { TODOLIST_TASK_STATUS } from '~/types/todolist'

import Badge from '../Badge'

import {
  IAP_SUB_TYPE_LABEL_MAPPING,
  IAP_PERIODICITY_LABEL_MAPPING,
  TEMPLATE_TYPE_LABEL_MAPPING,
  TEMPLATE_STATUS_LABEL_MAPPING,
  ASSET_STATUS_LABEL_MAPPING,
  CHECKLIST_STATUS_MAPPING,
  TODOLIST_TASK_STATUS_MAPPING,
  IAP_STATUS_LABEL_MAPPING,
  NAVIGATION_GRAPH_STATUS_LABEL_MAPPING,
  PAGE_CONTAINER_STATUS_LABEL_MAPPING,
  ML_LABEL_MAPPING,
  REVIEW_STATUS_MAPPING,
  REPORT_STATUS_MAPPING,
  REPORT_REASON_MAPPING,
  REPORT_COMMENT_STATUS_MAPPING,
  APP_USER_STATUS_MAPPING,
} from '~/const'
import { APP_EVENTS } from '~/types/event'
import { capitalize } from '~/utils/string'

import './styles.scss'

const AppUserStatusBadge = ({
  size = 'large',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: APP_USER_STATUS
}): ReactElement => {
  const label = APP_USER_STATUS_MAPPING[value]
  const componentClasses = classNames('app-user-status-badge', value)

  return <Badge className={componentClasses} size={size} value={label} />
}

const BuildStatusBadge = ({
  size = 'large',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: string
}): ReactElement => {
  const label = capitalize(value.split('_').join(' '))

  return <Badge className="build-status-badge" outlined size={size} value={label} />
}

const EventTypeBadge = ({
  eventType,
  size = 'large',
  value,
}: {
  eventType: TAppEventType
  size?: Exclude<SIZES, 'small'>
  value?: string
}): ReactElement | null => {
  const { backgroundColor, fontColor, label = 'Unknown event' } = APP_EVENTS[eventType] || {}

  return (
    <Badge
      className="event-type-badge"
      size={size}
      style={{ backgroundColor, color: fontColor }}
      value={value || label}
    />
  )
}

const IapStatusBadge = ({
  size = 'large',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: IAP_STATUS
}): ReactElement => {
  const componentClasses = classNames('iap-status-badge', value.toLowerCase())
  const label = IAP_STATUS_LABEL_MAPPING[value] || value

  return <Badge className={componentClasses} size={size} value={label} />
}

const IapTypeBadge = ({ size = 'large', value }: { size?: Exclude<SIZES, 'small'>; value: IAP_TYPE }): ReactElement => {
  const componentClasses = classNames('iap-type-badge', value.toLowerCase())
  const label = capitalize(value)

  return <Badge className={componentClasses} size={size} value={label} />
}

const IapSubTypeBadge = ({
  size = 'large',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: IAP_SUB_TYPE
}): ReactElement => {
  const componentClasses = classNames('iap-sub-type-badge', value.toLowerCase())
  const label = IAP_SUB_TYPE_LABEL_MAPPING[value] || value

  return <Badge className={componentClasses} size={size} value={label} />
}

const IapPeriodicityBadge = ({
  size = 'large',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: IAP_PERIODICITY
}): ReactElement => {
  const componentClasses = classNames('iap-periodicity-badge', `v-${value.toLowerCase()}`)
  const label = IAP_PERIODICITY_LABEL_MAPPING[value] || value

  return <Badge className={componentClasses} size={size} value={label} />
}

const TagBadge = ({
  className,
  size = 'large',
  value,
}: {
  className?: string
  size?: Exclude<SIZES, 'small'>
  value: string
}): ReactElement => {
  const componentClasses = classNames('tag-badge', className)

  return <Badge className={componentClasses} outlined size={size} value={value} />
}

const ChecklistStatusBadge = ({
  outlined,
  size = 'large',
  value,
}: {
  outlined?: boolean
  size?: Exclude<SIZES, 'small'>
  value: CHECKLIST_STATUS
}): ReactElement => {
  const componentClasses = classNames('checklist-status-badge', value)
  const status = CHECKLIST_STATUS_MAPPING[value]

  return <Badge className={componentClasses} outlined={outlined} size={size} value={status} />
}

const TodolistStatusBadge = ({
  outlined,
  size = 'large',
  value,
}: {
  outlined?: boolean
  size?: Exclude<SIZES, 'small'>
  value: TODOLIST_TASK_STATUS
}): ReactElement => {
  const componentClasses = classNames('todolist-status-badge', value)
  const status = TODOLIST_TASK_STATUS_MAPPING[value]

  return <Badge className={componentClasses} outlined={outlined} size={size} value={status} />
}

const ReviewMlLabelStatusBadge = ({
  outlined,
  size = 'medium',
  value,
}: {
  outlined?: boolean
  size?: Exclude<SIZES, 'small'>
  value: ML_LABEL
}): ReactElement => {
  const componentClasses = classNames('reviews-ml-status-badge', value)
  const status = ML_LABEL_MAPPING[value]

  return <Badge className={componentClasses} outlined={outlined} size={size} value={status} />
}

const ReportStatusBadge = ({
  outlined,
  size = 'medium',
  value,
}: {
  outlined?: boolean
  size?: Exclude<SIZES, 'small'>
  value: REPORT_STATUS
}): ReactElement => {
  const componentClasses = classNames('report-status-badge', value)
  const status = REPORT_STATUS_MAPPING[value]

  return <Badge className={componentClasses} outlined={outlined} size={size} value={status} />
}

const ReportCommentStatusBadge = ({
  outlined,
  size = 'medium',
  value,
}: {
  outlined?: boolean
  size?: Exclude<SIZES, 'small'>
  value: REPORT_COMMENT_STATUS
}): ReactElement => {
  const componentClasses = classNames('report-comment-status-badge', value)
  const status = REPORT_COMMENT_STATUS_MAPPING[value]

  return <Badge className={componentClasses} outlined={outlined} size={size} value={status} />
}

const ReportReasonBadge = ({
  outlined,
  size = 'medium',
  value,
}: {
  outlined?: boolean
  size?: Exclude<SIZES, 'small'>
  value: REPORT_REASON
}): ReactElement => {
  const reason = REPORT_REASON_MAPPING[value] || value

  return <Badge className="report-reason-badge" outlined={outlined} size={size} value={reason} />
}

const ReportCountBadge = ({
  size = 'medium',
  value,
}: {
  outlined?: boolean
  size?: Exclude<SIZES, 'small'>
  value: number
}): ReactElement => {
  const text = `${value} reports`
  return <Badge className="report-count-badge" size={size} value={text} />
}

const ReportTypeBadge = ({
  outlined,
  size = 'medium',
  value,
}: {
  outlined?: boolean
  size?: Exclude<SIZES, 'small'>
  value: string
}): ReactElement => {
  return <Badge className="report-type-badge" outlined={outlined} size={size} value={value} />
}

const ReviewStatusBadge = ({
  outlined,
  size = 'medium',
  value,
}: {
  outlined?: boolean
  size?: Exclude<SIZES, 'small'>
  value: REVIEW_STATUS
}): ReactElement => {
  const componentClasses = classNames('reviews-status-badge', value)
  const status = REVIEW_STATUS_MAPPING[value]

  return <Badge className={componentClasses} outlined={outlined} size={size} value={status} />
}

const ReviewTopicBadge = ({
  size = 'medium',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: string
}): ReactElement => {
  return <Badge className="review-topic-badge" outlined size={size} value={value} />
}

const VideoTagBadge = ({ size = 'medium', value }: { size?: Exclude<SIZES, 'small'>; value: string }): ReactElement => {
  return <Badge className="video-tag-badge" outlined size={size} value={value} />
}

const AssetStatusBadge = ({
  className,
  size = 'large',
  value,
}: {
  className?: string
  size?: Exclude<SIZES, 'small'>
  value: ASSET_STATUS
}): ReactElement | null => {
  const componentClasses = classNames('asset-status-badge', value, className)
  const label = ASSET_STATUS_LABEL_MAPPING[value]

  return <Badge className={componentClasses} size={size} value={label || 'Unknown'} />
}

const NavigationGraphBadge = ({
  size = 'large',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: NAVIGATION_GRAPH_STATUS
}): ReactElement | null => {
  const componentClasses = classNames('navigation-graph-status-badge', value)
  const label = NAVIGATION_GRAPH_STATUS_LABEL_MAPPING[value]

  return <Badge className={componentClasses} size={size} value={label || 'Unknown'} />
}

const PageContainerBadge = ({
  size = 'medium',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: PAGE_CONTAINER_STATUS
}): ReactElement => {
  const componentClasses = classNames('page-container-status-badge', value)
  const label = PAGE_CONTAINER_STATUS_LABEL_MAPPING[value]

  return <Badge className={componentClasses} size={size} value={label || 'Unknown'} />
}

const TemplateStatusBadge = ({
  size = 'large',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: TEMPLATE_STATUS
}): ReactElement => {
  const status = TEMPLATE_STATUS_LABEL_MAPPING[value] || 'Unknown'
  const componentClasses = classNames('template-status-badge', value)

  return <Badge className={componentClasses} size={size} value={status} />
}

const TemplateTypeBadge = ({
  size = 'large',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: TEMPLATE_TYPE
}): ReactElement => {
  const status = TEMPLATE_TYPE_LABEL_MAPPING[value] || 'Unknown'
  const componentClasses = classNames('template-type-badge', value)

  return <Badge className={componentClasses} size={size} value={status} />
}

const LokaliseProjectBadge = ({
  size = 'large',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: string
}): ReactElement => {
  const componentClasses = classNames('lokalise-project-badge', { common: value === 'Common Apps' })

  return <Badge className={componentClasses} size={size} value={value || 'Unknown'} />
}

const ABTestVariationBadge = ({
  size = 'medium',
  value,
}: {
  size?: Exclude<SIZES, 'small'>
  value: 'control' | 'variation'
}): ReactElement => {
  const componentClasses = classNames('ab-test-variation-badge', value)

  return <Badge className={componentClasses} size={size} value={value.toUpperCase()} />
}

const EnvironmentBadge = ({
  className,
  size = 'medium',
  value,
}: {
  className?: string
  size?: Exclude<SIZES, 'small'>
  value: 'dev' | 'prod'
}): ReactElement => {
  const componentClasses = classNames('environment-badge', value, className)

  return <Badge className={componentClasses} size={size} value={value} />
}

const VanillaBadge = ({
  className,
  outlined = false,
  value,
}: {
  className?: string
  outlined?: boolean
  value: string
}): ReactElement => {
  const componentClasses = classNames('vanilla-badge-component', className, {
    'vanilla-badge-component--outlined': outlined,
  })

  return (
    <div className={componentClasses}>
      <span className="vanilla-badge-component__text" title={value}>
        {value}
      </span>
    </div>
  )
}

export {
  ABTestVariationBadge,
  AppUserStatusBadge,
  AssetStatusBadge,
  BuildStatusBadge,
  ChecklistStatusBadge,
  EnvironmentBadge,
  EventTypeBadge,
  IapPeriodicityBadge,
  IapStatusBadge,
  IapSubTypeBadge,
  IapTypeBadge,
  LokaliseProjectBadge,
  NavigationGraphBadge,
  PageContainerBadge,
  ReportCommentStatusBadge,
  ReportCountBadge,
  ReportReasonBadge,
  ReportStatusBadge,
  ReportTypeBadge,
  ReviewMlLabelStatusBadge,
  ReviewStatusBadge,
  ReviewTopicBadge,
  TagBadge,
  TemplateStatusBadge,
  TemplateTypeBadge,
  TodolistStatusBadge,
  VanillaBadge,
  VideoTagBadge,
}
